import { useRef, useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Camera, Image as ImageIcon, X, MapPin, Calculator } from "lucide-react";
import { toast } from "sonner";
import { formatCLP } from "@/lib/gastos";

/** Imagen protegida: el endpoint exige token, así que se baja vía axios (con
 *  Authorization) y se muestra como object URL — un <img src> directo daría 401. */
export function AuthImage({ imageId, className = "", alt = "comprobante" }) {
  const { api } = useAuth();
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let url = null, alive = true;
    api.get(`/api/gastos/images/${imageId}`, { responseType: "blob" })
      .then((r) => { if (alive) { url = URL.createObjectURL(r.data); setSrc(url); } })
      .catch(() => {});
    return () => { alive = false; if (url) URL.revokeObjectURL(url); };
  }, [api, imageId]);
  if (!src) return <div className={`bg-gray-100 animate-pulse ${className}`} />;
  return <img src={src} alt={alt} className={className} />;
}

/**
 * Renderiza un campo del esquema dinámico de una categoría de gasto.
 *
 * Props:
 *  - campo:    definición del campo { key, label, tipo, obligatorio, opciones, config, placeholder, ayuda }
 *  - value:    valor actual del campo
 *  - onChange: (nuevoValor) => void
 *  - valores:  todos los valores del ítem (para 'distancia' que lee origen/destino)
 *  - disabled: solo lectura
 */
export default function DynamicField({ campo, value, onChange, valores = {}, disabled = false }) {
  const { api } = useAuth();
  const cameraRef = useRef(null);
  const galleryRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [calcLoading, setCalcLoading] = useState(false);

  const cfg = campo.config || {};
  const req = campo.obligatorio ? <span className="text-red-500 text-xs"> *</span> : null;
  const label = (
    <Label className="text-sm">
      {campo.label}{req}
      {campo.ayuda ? <span className="block text-[11px] text-gray-400 font-normal">{campo.ayuda}</span> : null}
    </Label>
  );

  // -------- Imagen --------
  const multiple = !!cfg.multiple;
  const images = multiple ? (Array.isArray(value) ? value : []) : (value ? [value] : []);

  const uploadImage = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/api/gastos/images/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const id = r.data.id;
      if (multiple) onChange([...(Array.isArray(value) ? value : []), id]);
      else onChange(id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al subir imagen");
    } finally {
      setUploading(false);
    }
  };

  const removeImage = async (id) => {
    try { await api.delete(`/api/gastos/images/${id}`); } catch (_) {}
    if (multiple) onChange((Array.isArray(value) ? value : []).filter((x) => x !== id));
    else onChange("");
  };

  // -------- Distancia --------
  const calcularDistancia = async () => {
    const origen = valores[cfg.origen];
    const destino = valores[cfg.destino];
    if (!origen || !destino) {
      toast.error("Ingresa origen y destino primero");
      return;
    }
    setCalcLoading(true);
    try {
      const r = await api.post("/api/gastos/distancia", { origen, destino });
      if (r.data.ok) { onChange(r.data.km); toast.success(`Distancia: ${r.data.km} km`); }
      else toast.error(r.data.detail || "No se pudo calcular; ingrésalo manualmente");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al calcular distancia");
    } finally {
      setCalcLoading(false);
    }
  };

  switch (campo.tipo) {
    case "textarea":
      return (
        <div>{label}
          <Textarea rows={3} value={value || ""} disabled={disabled}
            placeholder={campo.placeholder} className="mt-1 bg-white"
            onChange={(e) => onChange(e.target.value)} />
        </div>
      );

    case "numero":
      return (
        <div>{label}
          <Input type="text" inputMode="decimal" value={value ?? ""} disabled={disabled}
            placeholder={campo.placeholder} className="mt-1 h-11 bg-white no-uppercase"
            onChange={(e) => onChange(e.target.value.replace(/[^0-9.,-]/g, ""))} />
        </div>
      );

    case "monto":
      return (
        <div>{label}
          <div className="relative mt-1">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
            <Input type="text" inputMode="numeric" disabled={disabled}
              value={value ? Number(String(value).replace(/\D/g, "")).toLocaleString("es-CL") : ""}
              placeholder="0" className="pl-7 h-11 bg-white no-uppercase"
              onChange={(e) => onChange(e.target.value.replace(/\D/g, ""))} />
          </div>
        </div>
      );

    case "fecha":
      return (
        <div>{label}
          <Input type="date" value={value || ""} disabled={disabled}
            className="mt-1 h-11 bg-white no-uppercase"
            onChange={(e) => onChange(e.target.value)} />
        </div>
      );

    case "select":
      return (
        <div>{label}
          <Select value={value || ""} onValueChange={onChange} disabled={disabled}>
            <SelectTrigger className="mt-1 h-11 bg-white"><SelectValue placeholder={campo.placeholder || "Seleccionar"} /></SelectTrigger>
            <SelectContent className="bg-white">
              {(campo.opciones || []).map((o, i) => {
                const val = typeof o === "string" ? o : o.value;
                const lab = typeof o === "string" ? o : (o.label || o.value);
                return <SelectItem key={i} value={val}>{lab}</SelectItem>;
              })}
            </SelectContent>
          </Select>
        </div>
      );

    case "direccion":
      return (
        <div>{label}
          <div className="relative mt-1">
            <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input type="text" value={value || ""} disabled={disabled}
              placeholder={campo.placeholder || "Calle, número, comuna"} className="pl-9 h-11 bg-white"
              onChange={(e) => onChange(e.target.value)} />
          </div>
        </div>
      );

    case "distancia":
      return (
        <div>{label}
          <div className="flex items-center gap-2 mt-1">
            <Input type="text" inputMode="decimal" disabled={disabled}
              value={value ?? ""} placeholder="km" className="h-11 bg-white no-uppercase"
              onChange={(e) => onChange(e.target.value.replace(/[^0-9.,]/g, ""))} />
            <span className="text-xs text-gray-500 shrink-0">km</span>
            {!disabled && (
              <Button type="button" variant="outline" size="sm" onClick={calcularDistancia}
                disabled={calcLoading} className="shrink-0 h-11">
                {calcLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Calcular"}
              </Button>
            )}
          </div>
        </div>
      );

    case "calculo":
      return (
        <div>{label}
          <div className="mt-1 h-11 flex items-center gap-2 rounded-md border border-gray-200 bg-gray-50 px-3">
            <Calculator className="h-4 w-4 text-[#0FB4B7]" />
            <span className="text-sm font-medium text-gray-800">
              {(cfg.moneda || "CLP") === "CLP" ? formatCLP(value) : (value ?? 0)}
            </span>
            <span className="text-[10px] text-gray-400 ml-auto">automático</span>
          </div>
        </div>
      );

    case "imagen":
      return (
        <div>{label}
          <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden"
            onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; uploadImage(f); }} />
          <input ref={galleryRef} type="file" accept="image/*" className="hidden"
            onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; uploadImage(f); }} />
          <div className="mt-1 flex flex-wrap gap-2">
            {images.map((id) => (
              <div key={id} className="relative w-20 h-20 rounded-lg overflow-hidden border">
                <AuthImage imageId={id} className="w-full h-full object-cover" />
                {!disabled && (
                  <button type="button" onClick={() => removeImage(id)}
                    className="absolute top-0.5 right-0.5 bg-black/60 text-white rounded-full p-0.5">
                    <X className="h-3 w-3" />
                  </button>
                )}
              </div>
            ))}
            {!disabled && (multiple || images.length === 0) && (
              <div className="flex gap-2">
                <Button type="button" variant="outline" size="sm" className="h-20 w-20 flex-col gap-1"
                  onClick={() => cameraRef.current?.click()} disabled={uploading}>
                  {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />}
                  <span className="text-[10px]">Foto</span>
                </Button>
                <Button type="button" variant="outline" size="sm" className="h-20 w-20 flex-col gap-1"
                  onClick={() => galleryRef.current?.click()} disabled={uploading}>
                  <ImageIcon className="h-4 w-4" />
                  <span className="text-[10px]">Galería</span>
                </Button>
              </div>
            )}
          </div>
        </div>
      );

    case "texto":
    default:
      return (
        <div>{label}
          <Input type="text" value={value || ""} disabled={disabled}
            placeholder={campo.placeholder} className="mt-1 h-11 bg-white"
            onChange={(e) => onChange(e.target.value)} />
        </div>
      );
  }
}
